import { SvgIconComponent } from '@mui/icons-material';
import { SvgIconProps } from '@mui/material';
import { ZodValidationError } from '@occc/ui-forms';
import {
  awardExternalFulfillmentSchema,
  awardFuelRollbackSchema,
  awardProductDiscountSchema,
  awardSweepstakesEntrySchema,
  awardTransactionDiscountSchema,
} from '@occc-app/offerValidationService/schemas';
import { OfferSchema } from '@occc-app/offerValidationService/types';
import {
  FuelRollbackIcon,
  LocalMallOutlinedIcon,
  ShoppingBasketOutlinedIcon,
  ShoppingCartOutlinedIcon,
  StorefrontIcon,
} from '@occc/ui-design-system';

import {
  validateExternalFulfillmentForm,
  validateFuelRollbackForm,
  validateProductDiscountForm,
  validateSweepstakesForm,
  validateTransactionDiscountForm,
} from '../../schemas';

import { RequiresVoucherConstraintType, AWARD_TYPES } from '../../types';

export const constraintByType: {
  [key in AWARD_TYPES]: RequiresVoucherConstraintType;
} = {
  [AWARD_TYPES.PRODUCT]: 'ConstraintRequiresVoucherAvailableForDiscount',
  [AWARD_TYPES.TRANSACTION]: 'ConstraintRequiresVoucherAvailableForDiscount',
  [AWARD_TYPES.SWEEPSTAKES]: 'ConstraintRequiresVoucher',
  [AWARD_TYPES.FUEL_ROLLBACK]: 'ConstraintRequiresTransactionVoucher',
  [AWARD_TYPES.EXTERNAL_FULFILLMENT]: 'ConstraintRequiresVoucher',
};

export const offerSchemaByType: Record<AWARD_TYPES, OfferSchema> = {
  [AWARD_TYPES.PRODUCT]: awardProductDiscountSchema,
  [AWARD_TYPES.TRANSACTION]: awardTransactionDiscountSchema,
  [AWARD_TYPES.SWEEPSTAKES]: awardSweepstakesEntrySchema,
  [AWARD_TYPES.FUEL_ROLLBACK]: awardFuelRollbackSchema,
  [AWARD_TYPES.EXTERNAL_FULFILLMENT]: awardExternalFulfillmentSchema,
};

type OfferTypeItem = {
  type: AWARD_TYPES;
  title: string;
  description: string;
  icon: SvgIconComponent | React.ComponentType<SvgIconProps>;
};

export const offerTypes: OfferTypeItem[] = [
  {
    type: AWARD_TYPES.TRANSACTION,
    title: 'Transaction Discount',
    description: 'For example: 5% or $5 off the entire transaction',
    icon: LocalMallOutlinedIcon,
  },
  {
    type: AWARD_TYPES.PRODUCT,
    title: 'Product Discount',
    description: 'For example: Free coffee or 10¢ off coke',
    icon: ShoppingBasketOutlinedIcon,
  },
  {
    type: AWARD_TYPES.SWEEPSTAKES,
    title: 'Sweepstakes Entry',
    description: 'Grants one entry into a sweepstakes',
    icon: StorefrontIcon,
  },
  {
    type: AWARD_TYPES.FUEL_ROLLBACK,
    title: 'Fuel Rollback',
    description: 'Provide a cents per gallon fuel rollback to members at the pump',
    icon: FuelRollbackIcon,
  },
  {
    type: AWARD_TYPES.EXTERNAL_FULFILLMENT,
    title: 'Externally Fulfillable',
    description: 'Awards that are externally fulfillable',
    icon: ShoppingCartOutlinedIcon,
  },
];

export const supportedOrderAheadAwardTypes: AWARD_TYPES[] = [
  AWARD_TYPES.TRANSACTION,
  AWARD_TYPES.PRODUCT,
  AWARD_TYPES.EXTERNAL_FULFILLMENT,
];

export const awardConfigureValidatorByAwardType: Record<
  AWARD_TYPES,
  (value: unknown) => Promise<ZodValidationError>
> = {
  [AWARD_TYPES.TRANSACTION]: validateTransactionDiscountForm,
  [AWARD_TYPES.PRODUCT]: validateProductDiscountForm,
  [AWARD_TYPES.FUEL_ROLLBACK]: validateFuelRollbackForm,
  [AWARD_TYPES.EXTERNAL_FULFILLMENT]: validateExternalFulfillmentForm,
  [AWARD_TYPES.SWEEPSTAKES]: validateSweepstakesForm,
};
